/**
 * Who you are at the table: a name and a face.
 *
 * The second half of what used to be one join form. The code box moved to the
 * landing page, because that is what somebody arriving with a code in their
 * hand actually wants first.
 *
 * A rejected name comes back for another try rather than being silently
 * rewritten — a player whose name was changed without being told keeps
 * wondering why nobody reacts to it. The check itself is the server's; this
 * screen only mirrors the length limit so the obvious mistake does not cost a
 * round trip.
 *
 * ## Why this screen carries the brand and the interior pages do not
 *
 * Every other page behind the front door is reached *through* the front door.
 * This one is not: an invite is a link, and somebody handed one opens
 * `/join/F9M3` having never seen the landing page, the wordmark, or a single
 * word about what any of this is. So the lockup sits in the header here, and
 * the felt the landing page is built over carries on underneath — arriving by
 * link should look like arriving at the same place.
 */

import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { setProfile } from '../net/session.js';
import type { AvatarOption } from '../net/session.js';
import { useSession } from '../session-context.js';
import { playAvatar } from '../render/sound.js';
import { Icon } from '../render/Icon.js';
import { describeError } from './errors.js';
import { ActionBar, BackLink } from './ActionBar.js';
import { AvatarSheet } from './AvatarSheet.js';
import { Wardrobe } from './Wardrobe.js';

const CODE_KEY = 'cards.lastRoom';

/**
 * The header an arrival screen wears: the lockup, and room either side of it.
 *
 * The middle column is centered by the *grid*, not by its contents: `1fr auto
 * 1fr` puts the logo in the true middle of the header whether the side slots
 * hold anything or not. Same reasoning as the fixed icon columns in `.cta` —
 * centering a flex run centers the run, which is not the same thing. Both
 * slots are rendered empty rather than left out, because a grid with two
 * columns would put the logo in the middle of what is left over.
 *
 * It used to carry Back on the left and the sound toggle on the right. Back is
 * in the bar at the foot of the screen now, beside the button it is the
 * alternative to; sound is a device preference and has gone to sit with the
 * other settings.
 */
function ArrivalHead() {
  return (
    <header className="page-bar">
      <div className="page-bar-action" />

      <img
        className="arrival-logo"
        src="/art/logo-small.webp"
        width={320}
        height={97}
        /* The spoken name, not the written one: read literally, R3V3LRY is
           "R-three-V-three-L-R-Y", which is nobody's name for it. */
        alt="Revelry"
      />

      <div className="page-bar-action" />
    </header>
  );
}

export function JoinDetails() {
  const { code } = useParams<{ code: string }>();
  const { session, config } = useSession();
  const navigate = useNavigate();
  const location = useLocation();

  /*
   * The host comes through here too.
   *
   * They used to go straight from "start a game" into their own room, which
   * meant the one person who never passed this screen was the host - so they
   * had no name they chose and no face, and the table drew them with the
   * card-back fallback while everybody else had an avatar.
   *
   * `hosting` only changes what this screen says and offers: there is no
   * "just watch" for somebody who just opened the room, and the room already
   * exists, so the button is not "join".
   */
  const hosting = (location.state as { hosting?: boolean } | null)?.hosting === true;
  /* Told to us by `HostGame`, because this screen knows a room code and
     nothing else. Absent on every other way in, which is correct: you cannot
     arrive at somebody else's patience. */
  const solo = (location.state as { solo?: boolean } | null)?.solo === true;

  const [name, setName] = useState(config?.profile?.displayName ?? '');

  /*
   * A face is chosen FOR you, and you change it if you want to.
   *
   * Presenting the whole grid made picking one a required step, and the one
   * everybody skipped by taking whatever sat first - which was literally the
   * same face for every new player, because this defaulted to `avatars[0]`.
   * Somebody who does not care now gets a face anyway, and somebody who does
   * gets the grid by tapping the one they were given.
   *
   * Null until the config arrives. The initialiser cannot choose, because on
   * the very first render there is no list to choose from.
   */
  const [avatar, setAvatar] = useState<string | null>(config?.profile?.avatarId ?? null);
  const [picking, setPicking] = useState(false);

  useEffect(() => {
    if (avatar !== null || !config) return;

    // A face you already have beats a random one: coming back should not
    // silently reassign the face the table knows you by.
    const stored = config.profile?.avatarId;
    if (stored) {
      setAvatar(stored);
      return;
    }

    const options = config.avatars;
    if (options.length === 0) return;
    const choice = options[Math.floor(Math.random() * options.length)];
    if (choice) setAvatar(choice.id);
  }, [config, avatar]);
  /*
   * Play or watch, chosen before the name is submitted.
   *
   * An observer picks a name and a face too, because they are going to be
   * listed to the room by name - a table where people can watch invisibly is a
   * table nobody wants to play at.
   */
  const [watching, setWatching] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!code) return <NoCode />;
  if (!config || !session) return <div className="splash">Shuffling…</div>;

  const maxLength = config.name.maxLength;
  const tooLong = name.trim().length > maxLength;
  // The face is dealt rather than chosen, so it can be briefly absent - one
  // render, between the config arriving and the effect running.
  const chosen = config.avatars.find((option: AvatarOption) => option.id === avatar);
  const ready = name.trim().length > 0 && !tooLong && !busy && avatar !== null;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!ready || avatar === null) return;

    setBusy(true);
    setError(null);
    try {
      const saved = await setProfile(session, name.trim(), avatar);
      if (!saved.ok) {
        setError(saved.message ?? "That name isn't allowed here — try another.");
        return;
      }
      try {
        window.localStorage.setItem(CODE_KEY, code.toUpperCase());
      } catch {
        /* private browsing */
      }
      navigate(`/${watching ? 'watch' : 'play'}/${code.toUpperCase()}`);
    } catch (caught) {
      setError(describeError(caught, 'Could not save that. Try again?'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="page narrow felt-page join-page" onSubmit={submit}>
      {/*
        The faces, over the form rather than under it.

        Inline, the grid pushed the name you had just typed and the button you
        were heading for off the bottom of the phone - fine at twelve faces,
        unusable at seventy-two.
      */}
      {picking ? (
        <AvatarSheet
          chosen={avatar}
          onPick={(id) => {
            setAvatar(id);
            playAvatar(id);
          }}
          onClose={() => setPicking(false)}
        />
      ) : null}

      <ArrivalHead />

      <div className="arrival-body">
        {/* The code in gold, the way the hero's second line is. It is the one
            part of this heading anybody reads twice — checking it against the
            message that sent them here. */}
        {/*
          A patience has a room code, and nobody it would mean anything to.

          The code is here so a host can check it against the message that
          sent somebody else here. On a one-seat game there is no message and
          no somebody, so showing it makes the player look for a use it does
          not have - and the line under it offered to help them invite people
          to a game for one.
        */}
        <h1 className="page-title">
          {solo ? (
            'Your game'
          ) : (
            <>
              {hosting ? 'Your table is ' : 'Joining '}
              <span className="page-title-code">{code.toUpperCase()}</span>
            </>
          )}
        </h1>

        <p className="page-lede">
          {solo
            ? 'Pick how you look, then deal.'
            : hosting
              ? 'Pick how you look at the table, then invite the others.'
              : 'Pick how you look, then take your seat.'}
        </p>

        {/*
          One panel, rather than fields loose on the felt.

          The landing page puts every control on a surface, and that is what
          keeps a photograph from showing through the interface. A form is the
          case where it matters most: three fields floating directly on the felt
          had no edge at all between the questions and the picture behind them.
        */}
        <div className="form-card">
          {hosting ? null : (
            <div className="field">
              <span className="field-label">Are you playing?</span>
              {/*
                A segmented control, which is what the mockups draw for a choice
                between two or three. These used to be separate buttons that grew
                to fit their labels, so "Just watch" was the larger target purely
                for being the longer phrase.
              */}
              <div className="segmented" role="group" aria-label="Play or watch">
                <button
                  type="button"
                  className={`segment${watching ? '' : ' chosen'}`}
                  onClick={() => setWatching(false)}
                  aria-pressed={!watching}
                >
                  Play
                </button>
                <button
                  type="button"
                  className={`segment${watching ? ' chosen' : ''}`}
                  onClick={() => setWatching(true)}
                  aria-pressed={watching}
                >
                  Just watch
                </button>
              </div>
            </div>
          )}

          <label className="field">
            <span className="field-label">Your name</span>
            <input
              className="name-input"
              value={name}
              onChange={(event) => setName(event.target.value)}
              maxLength={maxLength + 4}
              placeholder="Sam"
              autoCorrect="off"
              autoFocus
            />
            {tooLong ? (
              <span className="field-error">{maxLength} characters or fewer, please.</span>
            ) : null}
          </label>

          <div className="field avatars">
            <span className="field-label">Your face</span>

            {/*
             * The face you were given, and the way to change it.
             *
             * `aria-expanded` rather than a bare button: this is a disclosure, and
             * somebody who cannot see the grid appear needs telling that tapping
             * opened something.
             */}
            <button
              type="button"
              className={`avatar-chosen${picking ? ' open' : ''}`}
              onClick={() => {
                setPicking(!picking);
                if (!picking && avatar) playAvatar(avatar);
              }}
              aria-expanded={picking}
              aria-label={`Your face: ${chosen?.label ?? 'choosing'}. Tap to change it.`}
            >
              <span className="avatar-chosen-face" aria-hidden="true">
                {chosen?.emoji ?? '🂠'}
              </span>
              <span className="avatar-chosen-hint">
                {chosen?.label ?? 'Dealing you a face…'}
                <span className="field-help"> — tap to change</span>
              </span>
            </button>

          </div>

          {/*
            The cards and the table, for the person supplying them.

            Here because this is already the screen where you settle how the
            evening looks, and a host should not have to open a game to find
            out they are dealing from the wrong deck. Only a host: the deck and
            the table belong to the room and come from whoever opened it (D87),
            so for a guest this would be a control over somebody else's table.

            `Wardrobe` draws nothing at all until a second theme is owned, so
            for almost everybody this screen is exactly what it was.
          */}
          {hosting ? <Wardrobe /> : null}
        </div>

        {error ? <p className="banner error">{error}</p> : null}
      </div>

      {/*
        Both ways out, pinned to the bottom.

        The submit used to be the last thing in the form, which meant it moved:
        opening the avatar grid pushed it down a row of faces, and a name too
        long pushed it again. The one control somebody is reaching for should
        not be somewhere new each time they look.

        Back goes to two different places. A guest arrives on a code from
        outside the app and has nothing behind them but the front door; a host
        reached here from their own games, two taps in, and sending them to the
        landing page was a dead end.
      */}
      <ActionBar>
        <BackLink to={hosting ? '/library' : '/'} label={hosting ? 'Your games' : 'Back'} />

        {/* The landing page's gold action, unchanged: this is the same promise
            "Start a Game" makes, seen from the other end of an invite. */}
        <button type="submit" className="cta cta-primary" disabled={!ready}>
          <Icon name="spade" className="cta-mark" />
          <span className="cta-label">
            {busy
              ? hosting
                ? 'Opening…'
                : 'Joining…'
              : hosting
                ? 'Open the table'
                : watching
                  ? 'Watch the table'
                  : "Let's play"}
          </span>
          <Icon name="chevron-right" />
        </button>
      </ActionBar>
    </form>
  );
}

function NoCode() {
  return (
    <div className="page narrow felt-page join-page">
      <ArrivalHead />
      <div className="arrival-body">
        <h1 className="page-title">Which room?</h1>
        <p className="page-lede">You need a room code to join a game.</p>
      </div>
      <ActionBar>
        <Link className="cta cta-primary" to="/">
          <Icon name="join-group" className="cta-mark" />
          <span className="cta-label">Enter a code</span>
          <Icon name="chevron-right" />
        </Link>
      </ActionBar>
    </div>
  );
}
